import { motion } from "framer-motion";
import { CheckCircle2, Award, Mic2, Users2, Clapperboard } from "lucide-react";

const BADGES = [
  { icon: <Award size={18} />, text: "DJ professionnel déclaré" },
  { icon: <Mic2 size={18} />, text: "Mariage · Anniversaire · Entreprise · Public" },
  { icon: <Users2 size={18} />, text: "Créateur Coulisse DJ" },
  { icon: <Clapperboard size={18} />, text: "Créateur de la série « Une journée avec un DJ » (YouTube)" },
  { icon: <CheckCircle2 size={18} />, text: "Défenseur du métier de DJ pro" },
];

export const Bio = () => {
  return (
    <section
      id="bio"
      data-testid="bio-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--bg-surface)] border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 relative"
        >
          {/* Soft aurora glow behind portrait */}
          <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-[radial-gradient(ellipse_at_30%_30%,rgba(255,45,156,0.25),transparent_60%),radial-gradient(ellipse_at_70%_70%,rgba(212,175,55,0.20),transparent_60%)] blur-2xl" />

          <div className="relative rounded-3xl overflow-hidden aspect-[4/5] glass">
            <img
              src="https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/8ukozayz_IMG_9795.jpeg"
              alt="Adrien Sebert – DJ professionnel"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] mb-1">
                Adrien Sebert
              </div>
              <div className="text-white font-semibold text-lg">DJ AS Animation · Coulisse DJ</div>
            </div>
          </div>
          <div className="absolute -top-6 -right-6 hidden md:block">
            <div className="glass rounded-2xl px-5 py-4 border border-[#FF2D9C]/20">
              <div className="text-2xl font-bold text-[#D4AF37]">20</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-400">
                Ans en cabine
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <span className="eyebrow">Qui suis-je ?</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Adrien Sebert. <br className="hidden md:block" />
            <span className="gold-text-gradient">DJ professionnel depuis plus de 20 ans.</span>
          </h2>
          <p className="mt-6 text-zinc-300 leading-relaxed text-base md:text-lg">
            J'accompagne les particuliers, entreprises et organisateurs d'événements
            pour créer des soirées sur mesure, avec une vraie attention portée à
            l'ambiance, au son, à la lumière et à l'expérience des invités.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Sur les réseaux, je partage les coulisses du métier sous le nom{" "}
            <span className="text-white font-medium">Coulisse DJ</span> — une
            communauté de plus de 258 000 passionnés qui suit mon quotidien et
            défend la profession.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Je suis aussi le créateur de la série{" "}
            <a
              href="#serie"
              data-testid="bio-series-link"
              className="text-white font-medium underline decoration-[#D4AF37]/60 underline-offset-4 hover:text-[#D4AF37] transition-colors"
            >
              « Une journée avec un DJ »
            </a>{" "}
            — plus de 100 épisodes disponibles sur YouTube pour découvrir les
            vraies coulisses d'une prestation, avant, pendant et après.
          </p>

          <div className="mt-9 grid sm:grid-cols-2 gap-3">
            {BADGES.map((b) => (
              <div
                key={b.text}
                className="flex items-start gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02]"
              >
                <span className="text-[#D4AF37] mt-0.5">{b.icon}</span>
                <span className="text-sm text-zinc-200">{b.text}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
